
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export const useCropDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [allCropData, setAllCropData] = useState<any[]>([]);
  const [cropOptions, setCropOptions] = useState({
    groups: [] as string[],
    locations: [] as string[]
  });

  console.log("🏗️ useCropDetails: Current state:", {
    isLoading,
    allCropDataLength: allCropData.length,
    groupsCount: cropOptions.groups.length,
    groups: cropOptions.groups
  });

  useEffect(() => {
    fetchAllCropData();
  }, []);

  const fetchAllCropData = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching all crop data from Supabase...");
      
      // Try fetching without any limit to get all records
      const { data, error } = await supabase
        .from("Crop Categories")
        .select("commodity, variety, group, Country, Region")
        .order('group', { ascending: true })
        .order('commodity', { ascending: true });

      if (error) {
        console.error("Supabase error:", error);
        toast({
          title: "Error",
          description: `Failed to fetch crop data: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      console.log(`Successfully loaded crop data: ${(data || []).length} total records`);
      console.log("Sample data records:", (data || []).slice(0, 5));
      
      if (data && data.length > 0) {
        setAllCropData(data);
        
        // Extract unique groups - handle potential null values
        const allGroups = data
          .map(item => item.group)
          .filter(group => group && group.trim().length > 0);
        const uniqueGroups = [...new Set(allGroups)].sort();
        
        console.log("📊 Data processing results:");
        console.log("- Raw groups found:", allGroups.length);
        console.log("- Unique groups found:", uniqueGroups.length);
        console.log("- All unique groups:", uniqueGroups);
        
        // Verify the groups are being set correctly
        console.log("🔧 Setting cropOptions with groups:", uniqueGroups);
        
        // Extract unique countries and regions for locations
        const allCountries = data
          .map(item => item.Country)
          .filter(country => country && country.trim().length > 0);
        const allRegions = data
          .map(item => item.Region) 
          .filter(region => region && region.trim().length > 0);
        
        // Combine unique countries and regions for location options
        const locationOptions = [
          ...new Set([...allCountries, ...allRegions])
        ].sort();
        
        // Fallback locations if no countries/regions in data
        const locations = locationOptions.length > 0 ? locationOptions : [
          "California", "Texas", "Florida", "Iowa", "Illinois", 
          "Nebraska", "Kansas", "Minnesota", "Indiana", "North Carolina"
        ];
        
        const newCropOptions = { groups: uniqueGroups, locations };
        setCropOptions(newCropOptions);
        
        console.log("✅ Final cropOptions set:", newCropOptions);
        
        toast({
          title: "Success",
          description: `Loaded ${uniqueGroups.length} crop groups from ${data.length} total records`,
        });
      } else {
        console.log("No data found in Crop Categories table");
        toast({
          title: "No Data",
          description: "No crop data found in the database",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "Failed to load crop data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCropTypesByGroup = useCallback((selectedGroup: string) => {
    if (!selectedGroup) return [];
    
    const filteredData = allCropData.filter(item => item.group === selectedGroup);
    const commodities = [...new Set(filteredData.map(item => item.commodity).filter(Boolean))];
    
    console.log(`Found ${commodities.length} crop types for group: ${selectedGroup}`);
    return commodities;
  }, [allCropData]);

  const getCropVarietiesByGroupAndType = useCallback((selectedGroup: string, selectedType: string) => {
    if (!selectedGroup || !selectedType) return [];
    
    const filteredData = allCropData.filter(
      item => item.group === selectedGroup && item.commodity === selectedType
    );
    const varieties = [...new Set(filteredData.map(item => item.variety).filter(Boolean))];
    
    console.log(`Found ${varieties.length} varieties for ${selectedGroup} - ${selectedType}`);
    return varieties;
  }, [allCropData]);

  const saveCropDetails = async (cropData: {
    crop: string;
    cropVariety: string;
    cropGroup: string;
    location: string;
  }) => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from("Crop Categories")
        .insert({
          commodity: cropData.crop,
          variety: cropData.cropVariety,
          group: cropData.cropGroup,
        });

      if (error) {
        console.error("Error saving:", error);
        toast({
          title: "Error",
          description: "Failed to save crop details",
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Crop details saved successfully!",
      });
      
      // Refresh data
      await fetchAllCropData();
      return true;
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save crop details",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    saveCropDetails,
    isLoading,
    cropOptions,
    getCropTypesByGroup,
    getCropVarietiesByGroupAndType
  };
};
